<template>
  <div class="swiper"
       :class="uniqueClass"
       v-bind="$attrs">
    <div
      class="swiper-wrapper"
      :class="smallerOuterSlides ? 'smaller-outer-slides' : ''">
      <slot name="default"></slot>
    </div>

    <div
      v-if="pagination"
      class="swiper-pagination"
      :class="paginationComputedClass"
      :style="`
        --swiper-pagination-bullet-size: var(--font-s);
        --swiper-pagination-bullet-horizontal-gap: var(--margin-m);
        --swiper-pagination-bullet-inactive-color: var(--c-gray-4);
        --swiper-pagination-bullet-inactive-opacity: 1;
        --swiper-pagination-color: var(--c-alt);
      `"></div>

    <slot v-if="!navigationOutside && showNavigation" name="navigation-prev">
      <div
        :class="navigationContainerClass"
        class="swiper-button swiper-button-prev border-radius-round"></div>
    </slot>
    <slot v-if="!navigationOutside && showNavigation" name="navigation-next">
      <div
        :class="navigationContainerClass"
        class="swiper-button swiper-button-next border-radius-round"></div>
    </slot>
  </div>
  <slot
    v-if="navigationOutside && swiperInstance.allowSlidePrev"
    name="navigation-prev"
    v-bind="{ swiperInstance }">
    <div
      :class="navigationContainerClass"
      class="swiper-button swiper-button-prev border-radius-round"
      @click="swiperInstance.slideNext()"></div>
  </slot>
  <slot
    v-if="navigationOutside && swiperInstance.allowSlideNext"
    name="navigation-next"
    v-bind="{ swiperInstance }"
    @click="next()">
    <div
      :class="navigationContainerClass"
      class="swiper-button swiper-button-next border-radius-round"
      @click="swiperInstance.slidePrev()"></div>
  </slot>
</template>

<script>
export default {
  props: {
    showNavigation: {
      type: Boolean,
      default: true,
    },
    navigationOutside: {
      type: Boolean,
      default: false,
    },
    smallerOuterSlides: {
      type: Boolean,
      default: false,
    },
    navigationThemeType: {
      type: String,
      default: "default", // "default","minimal"
    },
    navigationBgTheme: {
      type: String,
      default: "gray-2",
    },
    navigationTheme: {
      type: String,
      default: "alt",
    },
    paginationBgTheme: {
      type: String,
      default: "",
    },
    paginationTheme: {
      type: String,
      default: "bg",
    },
    paginationClass: {
      type: String,
      default: "",
    },
    pagination: {
      type: Boolean,
      default: false,
    },
    paginationDynamicBullets: {
      type: Boolean,
      default: false,
    },
    config: {},
  },
  data: function () {
    return {
      isSliding: false,
      Swiper: false,
      swiperInstance: false,
      uniqueClass:
        "slider-" + new Date().getTime() + "-" + utilities.getUniqueNumber(),
    };
  },
  computed: {
    navigationContainerClass() {
      const res = [];
      res.push(`c-${this.navigationTheme}`);

      res.push(`swiper-button--${this.navigationThemeType}`);

      if (this.navigationThemeType !== "minimal") {
        res.push(`bg-${this.navigationBgTheme}`);
      }

      if (this.navigationOutside) {
        res.push(`swiper-button--outside`);
      }

      return res;
    },
    // notice: this is not in use
    navigationIconClass() {
      const res = [];
      res.push(`c-${this.navigationTheme}`);
      res.push(`navigation--${this.navigationThemeType}`);

      if (this.navigationThemeType !== "minimal") {
        res.push(`bg-${this.navigationBgTheme}`);
      }

      return res;
    },
    paginationComputedClass() {
      const res = [this.paginationClass];
      res.push(`c-${this.paginationTheme}`);
      res.push(`bg-${this.paginationBgTheme}`);
      res.push(`swiper-button--${this.navigationThemeType}`);
      res.push(`${this.isLanguageRtl ? "rtl" : "ltr"}`);
      return res;
    },
  },
  async created() {
    const { css } = await import("swiper/swiper-bundle.css");
  },
  async mounted() {
    await this.loadSwiper();
    await utilities.wait(200);
    this.initSlider();
  },
  methods: {
    addClassToMiddleSlide() {
      // this has a bug where it will set two middle slides. it is compensated for with CSS
      const swiper = document.getElementsByClassName(this.uniqueClass)[0];
      if (!swiper) {
        return;
      }

      const visibleSlides = swiper.querySelectorAll(".swiper-slide-visible");
      if (!visibleSlides || visibleSlides.length < 3) {
        return;
      }
      let centerIndex = Math.trunc(visibleSlides.length / 2);

      visibleSlides.forEach((slide, index) => {
        slide.classList.remove("slide--middle");
        let isEven = visibleSlides.length % 2 === 0;
        // Length is even and the slide is "central"
        if (isEven && (index === centerIndex - 1 || index === centerIndex)) {
          slide.classList.add("slide--middle");
        }

        // length is not even and this is the middle slide
        if (!isEven && index === centerIndex) {
          slide.classList.add("slide--middle");
        }
      });
    },
    reset() {
      try {
        this.initSlider();
      } catch (e) {}
    },
    async loadSwiper() {
      const Swiper = await import("swiper/bundle");
      this.Swiper = Swiper.Swiper;

      return true;
    },
    async initSlider() {
      const this_ = this;
      if (this.swiperInstance) {
        this.swiperInstance.destroy();
        await utilities.wait(25);
      }

      let sliderConfig = {
        loop: true,
        slidesPerView: 1,
        spaceBetween: 20,
        observeParents: true,
        parallax: true,
        // Navigation arrows
        navigation: {
          nextEl: `.${this.uniqueClass} .swiper-button-next`,
          prevEl: `.${this.uniqueClass} .swiper-button-prev`,
        },
        slideChangeTransitionEnd: () => {
          this.isSliding = false;
        },

        ...this.config,
      };
      if (this.pagination) {
        sliderConfig.pagination = {
          el: ".swiper-pagination",
          dynamicBullets: this.paginationDynamicBullets,
        };
      }

      // todo: basic settings without complex config object - make some props
      this.swiperInstance = new this.Swiper(
        `.${this.uniqueClass}`,
        sliderConfig
      );
      if (this.smallerOuterSlides) {
        this.swiperInstance.on("slideChange", function () {
          this_.addClassToMiddleSlide();
        });

        this_.addClassToMiddleSlide();
      }
    },
    next() {
      this.swiperInstance.slideNext();

    /*
      try {
        if (this.swiperInstance.animation) {
          return false;
        }
        let targetIndex = this.swiperInstance.activeIndex + 1;
        let numSlides = this.swiperInstance.slides.length;
        if (targetIndex === numSlides) {
          targetIndex = 0;
        }

        this.swiperInstance.slideToLoop(targetIndex);
      } catch (e) {}
     */
    },
    prev() {
      console.log("prev");
      try {
        this.swiperInstance.slidePrev();
        /*
        if (this.swiperInstance.animation) {
          return false;
        }
        let targetIndex = this.swiperInstance.activeIndex - 1;
        let numSlides = this.swiperInstance.slides.length;
        if (targetIndex < 0) {
          targetIndex = numSlides - 1;
        }

        this.swiperInstance.slideToLoop(targetIndex);
        return true;
        */
      } catch (e) {}
    },
    getApi() {
      return this.swiperInstance;
    },
  },
};
</script>

<style scoped lang="scss">
.swiper-button {
  --swiper-navigation-size: calc(var(--base-margin) * 30);
  height: calc(var(--base-margin) * 50);
  width: calc(var(--base-margin) * 50);

  &--minimal {
    width: fit-content;
    background: transparent !important;
    --swiper-navigation-sides-offset: 0;
  }

  &--minimal.swiper-button--outside {
    border-radius: 0;
    top: 50%;
    transform: translateY(-50%);
    margin-top: 0;
  }
}

.smaller-outer-slides {
  :deep(.slide--middle) {
    .slide-content {
      transform: scale(1) !important;
    }
  }

  :deep(.swiper-slide-next) {
    .slide-content {
      transition: all 0.3s ease-in-out;
      @media (min-width: 766px) {
        transform: scale(
          0.8
        ) !important; // fix issue where there are two middle slides
      }
    }
  }

  :deep(.swiper-slide) {
    .slide-content {
      transition: all 0.3s ease-in-out;
      @media (min-width: 766px) {
        transform: scale(0.8);
      }
    }
  }
}

.disabled_swiper_button {
  opacity: 0;
  cursor: auto;
  pointer-events: none;
}
</style>
